import { loadNavigationAtom } from "../../../loadNavigation";
import { useSubconfigurationNavigationApi } from "../CustomSubconfigurationNavigation/SubconfigurationNavigationApi";
import { useAtomValue } from "jotai";

import { type JSX, useEffect, useEffectEvent, useRef } from "react";

import Grid from "@mui/material/Grid";
import {
  ThumbnailControlProps,
  useThumbnailControlLayoutProps,
  useVisibleAttributeValues,
} from "@plansoft-configuration-renderer/components";
import {
  ConfigurationValueController,
  useConfigurationResetKey,
  useCsAttribute,
  useSessionId,
} from "@plansoft-configuration-renderer/framework";

import { ThumbnailItem } from "./ThumbnailItem";
import { claimInitialLoad } from "./preselectionAutoAdvance";

interface CustomLayoutProps {
  custom: {
    renderAsSwitch?: boolean;
    renderAsCustomThumbnail?: boolean;
    nextAfterSelection?: boolean;
  };
}

/**
 * Thumbnail control
 * @category UI
 * @param props
 * @returns
 */
export function CustomThumbnailControl(
  props: ThumbnailControlProps & Partial<CustomLayoutProps>,
): JSX.Element {
  const values = useVisibleAttributeValues();
  const attribute = useCsAttribute();
  const resetKey = useConfigurationResetKey();
  const sessionId = useSessionId();
  const loadNavigation = useAtomValue(loadNavigationAtom);
  const { next, nextDisabled } = useSubconfigurationNavigationApi();
  const nextAfterSelection = props.custom?.nextAfterSelection === true;
  const renderAsSwitch = props.custom?.renderAsSwitch ?? false;
  const renderAsCustomThumbnail =
    props.custom?.renderAsCustomThumbnail ?? false;

  const { gridSpacing, gridDirection, gridBreakpoints } =
    useThumbnailControlLayoutProps();

  const className = renderAsSwitch
    ? "renderAsSwitch"
    : renderAsCustomThumbnail
      ? "renderAsCustomThumbnail"
      : "";

  // Preselected on load: advance once per configuration. Manual selections
  // are handled in ThumbnailActionArea.
  const isPreselectedAdvancePending = useRef(false);

  const claimPreselectedAdvance = useEffectEvent((): void => {
    isPreselectedAdvancePending.current =
      nextAfterSelection &&
      claimInitialLoad(resetKey, attribute.path) &&
      values.some((value) => value.isSelected);
  });

  useEffect(() => {
    claimPreselectedAdvance();
  }, []);

  // Wait for JumpToTab's load decision: when an opened configuration jumps
  // to the product view, a second navigation here would race it and leave
  // the configurator showing an empty step.
  useEffect(() => {
    if (
      !isPreselectedAdvancePending.current ||
      loadNavigation?.sessionId !== sessionId
    ) {
      return;
    }
    isPreselectedAdvancePending.current = false;
    if (
      !loadNavigation.jumpsToProductView &&
      values.some((value) => value.isSelected) &&
      !nextDisabled
    ) {
      next();
    }
  }, [loadNavigation, sessionId, values, nextDisabled, next]);

  return (
    <Grid
      container
      direction={gridDirection}
      spacing={gridSpacing}
      sx={{ justifyContent: renderAsCustomThumbnail ? "center" : "inherit" }}
    >
      {values.map((value) => (
        <Grid key={value.id} size={gridBreakpoints} className={className}>
          <ConfigurationValueController value={value}>
            <ThumbnailItem nextAfterSelection={nextAfterSelection}>
              {props.children}
            </ThumbnailItem>
          </ConfigurationValueController>
        </Grid>
      ))}
    </Grid>
  );
}
